<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $tc('messages.questions', 2),
                        href: meta.links.questions_index,
                    },
                    {
                        name: $t('messages.questions_per_tag_analysis'),
                        href: null,
                    },
                ]"
            />
        </template>

        <div class="w-full flex space-x-4 justify-end">
            <AppButton
                element="InertiaLink"
                :href="meta.links.questions_index"
                :title="$t('messages.question_list_button_tooltip')"
            >
                {{ $t('messages.questions_list') }}
            </AppButton>
        </div>

        <div>
            <div>
                {{ $tc('messages.stages', 1) }}
            </div>
            <div class="flex">
                <NavLinks :links="meta.nav_links.stages" />
            </div>
        </div>

        <EmptyState v-if="subjects.length === 0" picture />
        <div v-else class="flex flex-wrap gap-x-2 mt-3">
            <AppButton
                element="InertiaLink"
                :href="subject.url"
                v-for="subject in subjects"
                :key="subject.id"
                class="flex gap-x-2"
            >
                {{ subject.text }}

                <IconCircleCheckFilled class="h-5 w-5 text-indigo-600" v-if="filters.subject_id == subject.id" />
            </AppButton>
        </div>

        <div v-if="!!form.subject_id">
            <form action="" @submit.prevent="filter">
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-2">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.grade_level')" for="grade_level_id" />
                            <FormSelect
                                id="grade_level_id"
                                v-model="form.grade_level_id"
                                :options="options.grade_levels"
                                class="mt-1 w-full"
                                :null-option-text="$t('messages.all')"
                            />
                            <FormError :value="form.errors.grade_level_id" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-2">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.type')" for="type" />
                            <FormSelect
                                id="type"
                                v-model="form.type"
                                :options="options.types"
                                class="mt-1 w-full"
                                :null-option-text="$t('messages.all')"
                            />
                            <FormError :value="form.errors.type" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-2">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.quantity')" for="quantity" />
                            <FormInput
                                required
                                id="min_questions"
                                v-model.number="form.min_questions"
                                name="min_questions"
                                type="number"
                                step="1"
                                min="1"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.min_questions" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-2">
                        <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                        <div class="mt-1">
                            <SubmitFormButton
                                :button-text="$t('messages.filter')"
                                :processing="form.processing"
                                is-filter
                            />
                        </div>
                    </div>
                </div>
            </form>

            <div v-if="contentBlocks.length" class="mt-5">
                <div class="md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-6 mt-3">
                    <div v-for="block in contentBlocks" :key="block.id">
                        <AppWell>
                            <AppCard>
                                <div class="p-1">
                                    <div class="font-bold">
                                        {{ block.name }}
                                    </div>
                                    <div class="pl-3 divide-y divide-gray-300">
                                        <div class="mt-3" v-for="section in block.sections" :key="section.name">
                                            <div class="mt-3">
                                                {{ section.name }}
                                            </div>
                                            <div class="pl-3">
                                                <div
                                                    v-for="tag in section.tags"
                                                    :key="tag.id"
                                                    class="mt-1 flex items-center gap-x-2"
                                                >
                                                    <div>
                                                        <Tippy>
                                                            <button>
                                                                <MedallionLevels
                                                                    class="w-10 h-10"
                                                                    :levels-info="tag.levels.quantities"
                                                                />
                                                            </button>
                                                            <template #content>
                                                                <div class="mt-1">
                                                                    <div
                                                                        class="mt-3"
                                                                        v-for="level in tag.levels.levels"
                                                                        :key="level.level"
                                                                    >
                                                                        <div>
                                                                            {{ $tc('messages.levels', 1) }}
                                                                            {{ level.difficulty }}
                                                                        </div>
                                                                        <div class="text-xs">
                                                                            {{
                                                                                $tc(
                                                                                    'messages.questions',
                                                                                    level.quantity,
                                                                                )
                                                                            }}:
                                                                            {{ level.quantity }}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div class="mt-5">
                                                                    {{ $t('messages.total') }}: {{ tag.total }}
                                                                </div>
                                                            </template>
                                                        </Tippy>
                                                    </div>
                                                    <AppButton
                                                        element="InertiaLink"
                                                        :href="tag.url"
                                                        :title="tag.description"
                                                    >
                                                        {{ tag.name }}
                                                    </AppButton>
                                                    <div v-if="tag.links.length">
                                                        <TagHelpSlideOver :tag="tag" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </AppCard>
                        </AppWell>
                    </div>
                </div>
            </div>
            <EmptyState v-else-if="!!filters.difficulty" picture />
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import EmptyState from '@/components/EmptyState.vue';
import AppButton from '@/components/AppButton.vue';
import { IconCircleCheckFilled } from '@tabler/icons-vue';
import AppCard from '@/Shared/AppCard.vue';
import AppWell from '@/Shared/AppWell.vue';
import { Tippy } from 'vue-tippy';
import MedallionLevels from '@/components/MedallionLevels.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import TagHelpSlideOver from '../../../Student/Activity/Practice/partials/TagHelpSlideOver.vue';
import NavLinks from '../../../../components/NavLinks.vue';

export default {
    components: {
        NavLinks,
        TagHelpSlideOver,
        SubmitFormButton,
        FormInput,
        FormError,
        FormSelect,
        FormLabel,
        MedallionLevels,
        Tippy,
        AppWell,
        AppCard,
        AppButton,
        PanelPage,
        EmptyState,
        IconCircleCheckFilled,
    },
    layout: Layout,
    name: 'Index',
    props: ['subjects', 'contentBlocks', 'filters', 'options', 'meta'],
    data() {
        return {
            form: this.$inertia.form({
                subject_id: this.filters.subject_id,
                grade_level_id: this.filters.grade_level_id,
                min_questions: this.filters.min_questions,
                type: this.filters.type,
            }),
            showAll: false,
        };
    },
    watch: {
        'form.difficulty': function (newVal) {
            if (newVal) {
                this.filter();
            }
        },
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
    },
};
</script>
